import React from 'react';
import classNames from 'classnames';
import {
  Text,
  View,
  Pressable,
  StyleProp,
  TextStyle,
} from 'react-native';

import formatInitials from '../modules/formatters/formatInitials';

// We should be migrating to the module. Move styles as it becomes possible.
import './AvatarIcon.scss';
import { clickable } from '../styles';
// NOTE: although the linter says that `styles` is unused, it's needed to differentiate from the
// other stylesheet in react native. Please do not remove.

export type AvatarIconProps = {
  /** Image to show as the avatar */
  icon?: string;
  /** Text to use to pick default initials for the avatar */
  name?: string;
  /** Function called when the avatar is clicked */
  onClick?: () => void;
  /** DEPRECATED className to apply to the avatar */
  className?: string;
  /** DEPRECATED: className to apply to the component */
  componentClassName?: string;
  /** Size of the component:
   * - extra-xx-small: 24x24
   * - extra-extra-small: 32x32
   * - extra-small: 37x37
   * - small: 41x41
   * - medium-small: 44x44
   * - medium: 60x60
   * - medium-large: 77x77
   * - large: 89x89
   */
  sizeClass?: string;
  /** Text to show as a badge */
  badge?: string;
  /** Style to apply to the badge */
  badgeTextStyle?: StyleProp<TextStyle>;
  /** Function called when the badge is clicked */
  onBadgeClick?: () => void;
  /** The border color around the icon */
  iconBorderColor?: string;
  /** Shows the drop shadow */
  showBoxShadow?: boolean;
  /** Test ID */
  testID?: string;
};

/**
 * Creates an avatar icon (currently a circle) which either shows
 * an image for the item (pro or client) or their initials.
 *
 * Usage
 * -----
 * ```
 *   <AvatarIcon
 *     icon="https://d1zn1kfr9netj0.cloudfront.net/static/uploads/2016/09/28/125f6e3f-f85_20_54x54.jpg"
 *     name="John Doe"
 *     onClick="this.goToClientFn()" />
 * ```
 */
const AvatarIcon = ({
  icon = '',
  name = '',
  onClick = null,
  className = '',
  componentClassName = '',
  sizeClass = '',
  badge,
  badgeTextStyle = {},
  onBadgeClick,
  iconBorderColor,
  testID = 'avatar-icon',
  showBoxShadow = false,
}: AvatarIconProps) => {
  const isDefaultImage = (icon && icon.indexOf('images/default') >= 0);

  // Type any, as is styling properties that don't explicitly exist on the type
  // StyleProp<TextStyle>
  // eslint-disable-line @typescript-eslint/no-explicit-any
  let avatarStyles: any = {};
  let initials = formatInitials(name, 2);

  if (icon && !isDefaultImage) {
    initials = '';
    avatarStyles = { backgroundImage: `url(${icon})` };
  }

  if (iconBorderColor) {
    avatarStyles.borderWidth = 2;
    avatarStyles.borderStyle = 'solid';
    avatarStyles.borderRadius = '50%';
    avatarStyles.borderColor = iconBorderColor;
  }

  if (showBoxShadow) {
    avatarStyles.boxShadow = '0px 4px 4px rgba(18, 17, 17, 0.15)';
  }

  const allClasses = classNames('avatar-icon', className, sizeClass);

  // Make sure component is wrapped with Pressable only if onClick is defined
  const Wrapper = onClick ? Pressable : View;

  return (
    <Wrapper onPress={onClick} testID={testID}>
      <View
        className={classNames('avatar avatar-component', componentClassName)}
        style={onClick ? clickable : null}
      >
        <Text className={allClasses} style={avatarStyles}>
          {initials}
          {!!badge && (
            <View>
              <View>
                <Pressable onPress={onBadgeClick}>
                  <View style={clickable}>
                    <Text style={badgeTextStyle}>
                      {badge}
                    </Text>
                  </View>
                </Pressable>
              </View>
            </View>
          )}
        </Text>
      </View>
    </Wrapper>
  );
};

export default AvatarIcon;
